import React, { useState, useEffect } from 'react';
import { useCuffaStore } from 'stores/cuffa/cuffa.store';
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useParams } from 'react-router-dom';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { ROUTES } from 'routes/constants';

export enum CuffaMeasurementUnitEnum {
  QUILOGRAMAS = 'QUILOGRAMAS',
  TONELADAS = 'TONELADAS',
  LITROS = 'LITROS',
  UNIDADES = 'UNIDADES',
  CABECAS = 'CABEÇAS',
  MIL_FRUTOS = 'MIL FRUTOS',
  ARROBAS = 'ARROBAS',
  MACOS = 'MAÇOS',
  FARDOS = 'FARDOS',
  CAIXAS = 'CAIXAS',
  MIL_LITROS = 'MIL LITROS',
}

// Mapeamento dos meses da API para exibição
export const MES_MAP_VIEW: { [key: string]: string } = {
  january: 'Jan', february: 'Fev', march: 'Mar', april: 'Abr', may: 'Mai', june: 'Jun',
  july: 'Jul', august: 'Ago', september: 'Set', october: 'Out', november: 'Nov', december: 'Dez'
};

// Mapeamento das unidades de medida para nomes legíveis
export const unitNameMap: Record<CuffaMeasurementUnitEnum, string> = {
  [CuffaMeasurementUnitEnum.QUILOGRAMAS]: 'KG',
  [CuffaMeasurementUnitEnum.TONELADAS]: 'T',
  [CuffaMeasurementUnitEnum.LITROS]: 'L',
  [CuffaMeasurementUnitEnum.UNIDADES]: 'Un',
  [CuffaMeasurementUnitEnum.CABECAS]: 'Cabeças',
  [CuffaMeasurementUnitEnum.MIL_FRUTOS]: 'Mil Frutos',
  [CuffaMeasurementUnitEnum.ARROBAS]: 'Arrobas',
  [CuffaMeasurementUnitEnum.MACOS]: 'Maços',
  [CuffaMeasurementUnitEnum.FARDOS]: 'Fardos',
  [CuffaMeasurementUnitEnum.CAIXAS]: 'Caixas',
  [CuffaMeasurementUnitEnum.MIL_LITROS]: 'Mil Litros',
};

// Interface para os dados de um único produto convencional
interface IProdutoConvencional {
  id: string;
  nome: string;
  origem: 'animal' | 'vegetal' | 'outro';
  areaHectares: number | null;
  volumeProduzido: number | null;
  unidadeVolume: CuffaMeasurementUnitEnum;
  volumeComercializado: number | null;
  valorComercializado: number | null;
  volumeConsumido: number | null;
  volumePerdido: number | null;
  mesesDisponibilidade: {
    anoTodo: boolean;
    meses: string[];
  };
}

// Interface para os dados completos da tela
interface IProducaoConvencionalData {
  praticaConvencional: boolean;
  produtos: IProdutoConvencional[];
}

// Função auxiliar para formatar valores monetários
export const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return 'Não informado';
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

// Função auxiliar para formatar números
export const formatNumber = (value: number | null | undefined) => {
  if (value === null || value === undefined) return 'Não informado';
  return value.toLocaleString('pt-BR');
};

// Função para mapear dados locais (store) para IProducaoConvencionalData
export const mapLocalToProducao = (local: any): IProducaoConvencionalData => {
  const produtosLocais = local.conventional_products;
  const origemMap = (p: any) => {
    const pt = (p.production_type || '').toLowerCase();
    if (pt === 'animal' || pt === 'vegetal') return pt;
    return 'outro';
  };
  const mappedData: IProducaoConvencionalData = {
    praticaConvencional: !!local.practices_conventional_production,
    produtos: produtosLocais.map((p: any): IProdutoConvencional => {
      let availabilityMonths = p.availability_months;
      if (typeof availabilityMonths === 'string') {
        try {
          availabilityMonths = JSON.parse(availabilityMonths);
        } catch (e) {
          availabilityMonths = {};
        }
      }
      const mesesSelecionados: string[] = [];
      if (availabilityMonths && !availabilityMonths.available_year_round) {
        for (const [key, value] of Object.entries(availabilityMonths)) {
          if (value === true && MES_MAP_VIEW[key]) {
            mesesSelecionados.push(MES_MAP_VIEW[key]);
          }
        }
      }
      let unidadeVolume: CuffaMeasurementUnitEnum;
      try {
        unidadeVolume = p.volume_produced_unit as CuffaMeasurementUnitEnum;
        if (!Object.values(CuffaMeasurementUnitEnum).includes(unidadeVolume)) {
          unidadeVolume = CuffaMeasurementUnitEnum.QUILOGRAMAS;
        }
      } catch (e) {
        unidadeVolume = CuffaMeasurementUnitEnum.QUILOGRAMAS;
      }
      return {
        id: p.uuid || p.product_id,
        nome: p.name || p.custom_product_name || 'Produto Convencional',
        origem: origemMap(p),
        areaHectares: p.production_area_hectares ?? null,
        volumeConsumido: p.volume_self_consumption ?? null,
        volumeProduzido: p.volume_produced ?? null,
        volumeComercializado: p.volume_commercialized ?? null,
        unidadeVolume: unidadeVolume,
        valorComercializado: p.average_commercialized_value ?? null,
        volumePerdido: p.volume_lost ?? null,
        mesesDisponibilidade: {
          anoTodo: availabilityMonths?.available_year_round || false,
          meses: mesesSelecionados,
        },
      };
    }),
  };
  return mappedData;
};

// Função para mapear dados da API para IProducaoConvencionalData
export const mapApiToProducao = (apiData: any): IProducaoConvencionalData => {
  const origemMap = (p: any) => {
    const pt = (p.production_type || '').toLowerCase();
    if (pt === 'animal' || pt === 'vegetal') return pt;
    return 'outro';
  };
  const mappedData: IProducaoConvencionalData = {
    praticaConvencional: apiData.family_practices_conventional_production || false,
    produtos: (apiData.products || [])
      .filter((p: any) => p.management_type === 'CONVENCIONAL')
      .map((p: any): IProdutoConvencional => {
        let availabilityMonths = p.available_months;
        if (typeof availabilityMonths === 'string') {
          try {
            availabilityMonths = JSON.parse(availabilityMonths);
          } catch (e) {
            availabilityMonths = {};
          }
        }
        const mesesSelecionados: string[] = [];
        if (availabilityMonths && !availabilityMonths.available_year_round) {
          for (const [key, value] of Object.entries(availabilityMonths)) {
            if (value === true && MES_MAP_VIEW[key]) {
              mesesSelecionados.push(MES_MAP_VIEW[key]);
            }
          }
        }
        let unidadeVolume: CuffaMeasurementUnitEnum;
        try {
          unidadeVolume = p.measurement_unit as CuffaMeasurementUnitEnum;
          if (!Object.values(CuffaMeasurementUnitEnum).includes(unidadeVolume)) {
            unidadeVolume = CuffaMeasurementUnitEnum.QUILOGRAMAS;
          }
        } catch (e) {
          unidadeVolume = CuffaMeasurementUnitEnum.QUILOGRAMAS;
        }
        return {
          id: p.uuid,
          nome: p.custom_product_name || 'Produto Convencional',
          origem: origemMap(p),
          areaHectares: parseFloat(p.area_hectares) || null,
          volumeConsumido: parseFloat(p.volume_consumed_donated) || null,
          volumeProduzido: parseFloat(p.volume_produced) || null,
          volumeComercializado: parseFloat(p.volume_commercialized) || null,
          unidadeVolume: unidadeVolume,
          valorComercializado: parseFloat(p.commercialized_value) || null,
          volumePerdido: parseFloat(p.volume_lost) || null,
          mesesDisponibilidade: {
            anoTodo: availabilityMonths?.available_year_round || false,
            meses: mesesSelecionados,
          },
        };
      }),
  };
  return mappedData;
};

export const ProducaoConvencionalVisualizar: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();

  const [dados, setDados] = useState<IProducaoConvencionalData | null>(null);
  const [loading, setLoading] = useState(true);
  const dadosParciais = useCuffaStore(state => state.dadosParciais);

  useEffect(() => {
    const fetchData = async () => {
      if (!uuid) {
        setLoading(false);
        return;
      }

      const local = dadosParciais?.producaoConvencional;
      if (local && Array.isArray(local.conventional_products)) {
        setDados(mapLocalToProducao(local));
        setLoading(false);
        return;
      }

      // Se não houver storage, buscar da API
      try {
        const response = await CuffaAPI.getDataByID(uuid);
        if (response && response.dados) {
          const apiData = response.dados;
          setDados(mapApiToProducao(apiData));
        } else {
          setDados(null);
        }
      } catch (error) {
        setDados(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [uuid, dadosParciais]);

  if (loading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Produção Convencional" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  if (!dados) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Produção Convencional" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" margin="2rem 0">
          <Typography>Não foi possível carregar os dados. Verifique o console para mais detalhes.</Typography>
        </Box>
      </MenuProvider>
    );
  }

  const produtosAnimais = dados.produtos.filter(p => p.origem === 'animal');
  const produtosVegetais = dados.produtos.filter(p => p.origem === 'vegetal');
  const produtosOutros = dados.produtos.filter(p => p.origem === 'outro');

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Visualização de Produção Convencional" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Produção Convencional" width="100%">
            <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                A unidade familiar pratica produção convencional?
              </Typography>
              <Typography variant="body1">
                {dados.praticaConvencional ? 'Sim' : 'Não'}
              </Typography>
            </Paper>

            {dados.praticaConvencional && (
              <>
                {produtosAnimais.length > 0 && (
                  <Box mb={4}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Produtos de Origem Animal (Convencional)
                    </Typography>
                    {produtosAnimais.map(produto => (
                      <DetalhesProdutoConvencional key={produto.id} produto={produto} />
                    ))}
                  </Box>
                )}

                {produtosVegetais.length > 0 && (
                  <Box mb={4}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Produtos de Origem Vegetal (Convencional)
                    </Typography>
                    {produtosVegetais.map(produto => (
                      <DetalhesProdutoConvencional key={produto.id} produto={produto} />
                    ))}
                  </Box>
                )}

                {produtosOutros.length > 0 && (
                  <Box mb={4}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Produtos Convencionais
                    </Typography>
                    {produtosOutros.map(produto => (
                      <DetalhesProdutoConvencional key={produto.id} produto={produto} />
                    ))}
                  </Box>
                )}

                {dados.produtos.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum produto convencional foi registrado.
                  </Typography>
                )}
              </>
            )}

            <Box marginTop="2rem" display="flex" justifyContent="center" gap="1rem">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(ROUTES.PRODUCAO_AGROECOLOGICA_VISUALIZAR.replace(':uuid', uuid ?? ''))}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (uuid) {
                    navigate(ROUTES.PRODUTOS_PROCESSADOS_VISUALIZAR.replace(':uuid', uuid));
                  }
                }}
              >
                Avançar
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>
    </MenuProvider>
  );
};

// Subcomponente para exibir os detalhes de cada produto convencional
const DetalhesProdutoConvencional: React.FC<{ produto: IProdutoConvencional }> = ({ produto }) => (
  <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: '8px' }}>
    <Typography variant="h6" fontWeight="bold">
      {produto.nome}
    </Typography>
    <Divider sx={{ mb: 2 }} />
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" fontWeight="bold">Área de Produção (hectares):</Typography>
        <Typography variant="body1">{formatNumber(produto.areaHectares) || 'Não informado'} ha</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" fontWeight="bold">Volume Produzido (último ano):</Typography>
        <Typography variant="body1">
          {formatNumber(produto.volumeProduzido) || 'Não informado'}
          {' '}{unitNameMap[produto.unidadeVolume] || produto.unidadeVolume}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" fontWeight="bold">Volume Comercializado (último ano):</Typography>
        <Typography variant="body1">
          {formatNumber(produto.volumeComercializado) || 'Não informado'}
          {' '}{unitNameMap[produto.unidadeVolume] || produto.unidadeVolume}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" fontWeight="bold">Valor Comercializado (R$):</Typography>
        <Typography variant="body1">{formatCurrency(produto.valorComercializado)}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" fontWeight="bold">Volume Consumido/Doado/Trocado:</Typography>
        <Typography variant="body1">
          {formatNumber(produto.volumeConsumido) || 'Não informado'}
          {' '}{unitNameMap[produto.unidadeVolume] || produto.unidadeVolume}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" fontWeight="bold">Volume Perdido:</Typography>
        <Typography variant="body1">
          {formatNumber(produto.volumePerdido) || 'Não informado'}
          {' '}{unitNameMap[produto.unidadeVolume] || produto.unidadeVolume}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography fontWeight="bold" variant="subtitle2">Meses de Disponibilidade:</Typography>
        {produto.mesesDisponibilidade.anoTodo ? (
          <Chip color="success" label="Disponível o Ano Todo" size="small" />
        ) : (
          <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
            {produto.mesesDisponibilidade.meses.length > 0 ? (
              produto.mesesDisponibilidade.meses.map(mes => <Chip key={mes} label={mes} size="small" />)
            ) : (
              <Typography variant="body2" color="text.secondary">Nenhum mês especificado.</Typography>
            )}
          </Box>
        )}
      </Grid>
    </Grid>
  </Paper>
);

export default ProducaoConvencionalVisualizar;
